import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Alert, Box, Button, FormControl, FormLabel, Input, List, ListItem, Modal, ModalDialog, Typography } from '@mui/joy';
import axiosInstance from '../util/axiosConfig';

const DEBOUNCE_MS = 300;

/**
 * Searchable size dropdown for implants.
 * value = size_id (number or ''), onChange(value) with value as string id or ''.
 */
const SizeSelect = ({ value, onChange, label = 'Size', required = false, size: inputSize = 'md', placeholder = 'Search or select size...', sx }) => {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [addSizeOpen, setAddSizeOpen] = useState(false);
  const [newSize, setNewSize] = useState({ size: '', desc: '' });
  const [addSizeSubmitting, setAddSizeSubmitting] = useState(false);
  const [addSizeError, setAddSizeError] = useState('');
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  const fetchSizes = useCallback(async (term = '') => {
    setLoading(true);
    try {
      const params = term ? { search: term } : {};
      const res = await axiosInstance.get('/implant-sizes', { params });
      setOptions(res.data.sizes || []);
    } catch (err) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const delay = search ? DEBOUNCE_MS : 0;
    debounceRef.current = setTimeout(() => {
      fetchSizes(search);
    }, delay);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, open, fetchSizes]);

  // When value (size_id) changes from parent, try to resolve label from options
  useEffect(() => {
    if (value && options.length > 0) {
      const o = options.find((s) => Number(s.id) === Number(value));
      if (o) setSelectedLabel(o.size);
      else setSelectedLabel('');
    } else if (!value) {
      setSelectedLabel('');
    }
  }, [value, options]);

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 200);
  };

  const handleSelect = (sizeId, labelText) => {
    if (sizeId === '__add_new__') {
      setAddSizeOpen(true);
      setNewSize({ size: '', desc: '' });
      setAddSizeError('');
      return;
    }
    onChange(sizeId != null ? String(sizeId) : '');
    setSelectedLabel(labelText || '');
    setSearch('');
    setOpen(false);
  };

  const handleAddSizeSubmit = async (e) => {
    e.preventDefault();
    if (!newSize.size?.trim()) {
      setAddSizeError('Size is required');
      return;
    }
    setAddSizeSubmitting(true);
    setAddSizeError('');
    try {
      const res = await axiosInstance.post('/implant-sizes', {
        size: newSize.size.trim(),
        desc: newSize.desc?.trim() || null
      });
      const created = res.data.size;
      setAddSizeOpen(false);
      setNewSize({ size: '', desc: '' });
      await fetchSizes('');
      onChange(String(created.id));
      setSelectedLabel(created.size);
      setOpen(false);
    } catch (err) {
      setAddSizeError(err.response?.data?.error || 'Failed to add size');
    } finally {
      setAddSizeSubmitting(false);
    }
  };

  const displayValue = open ? search : (selectedLabel || (value && options.find((s) => Number(s.id) === Number(value))?.size) || '');

  return (
    <FormControl sx={{ minWidth: 200, ...sx }} ref={containerRef}>
      <FormLabel>{label}{required ? ' *' : ''}</FormLabel>
      <Input
        value={displayValue}
        onChange={(e) => {
          setSearch(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        placeholder={loading ? 'Loading...' : placeholder}
        size={inputSize}
        sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
      />
      {open && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 0.5,
            maxHeight: 220,
            overflow: 'auto',
            bgcolor: 'background.surface',
            border: '1px solid',
            borderColor: 'neutral.outlinedBorder',
            borderRadius: 'sm',
            boxShadow: 'md',
            zIndex: 1100
          }}
        >
          <List size={inputSize}>
            {options.length === 0 && !loading && (
              <ListItem>
                <Typography level="body-sm" color="neutral">
                  No sizes found. Add sizes from settings or try another search.
                </Typography>
              </ListItem>
            )}
            {options.map((s) => (
              <ListItem
                key={s.id}
                onClick={() => handleSelect(s.id, s.size)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(29, 27, 77, 0.1)' },
                  ...(Number(s.id) === Number(value) ? { bgcolor: 'rgba(29, 27, 77, 0.12)' } : {})
                }}
              >
                <Box>
                  <Typography level="body-sm" fontWeight={500}>{s.size}</Typography>
                  {s.desc && (
                    <Typography level="body-xs" color="neutral">{s.desc}</Typography>
                  )}
                </Box>
              </ListItem>
            ))}
            <ListItem
              onClick={() => handleSelect('__add_new__')}
              sx={{
                cursor: 'pointer',
                borderTop: '1px solid',
                borderColor: 'neutral.outlinedBorder',
                '&:hover': { bgcolor: 'rgba(29, 27, 77, 0.08)' }
              }}
            >
              <Typography level="body-sm" sx={{ color: 'primary.500', fontWeight: 500 }}>+ Add new size</Typography>
            </ListItem>
          </List>
        </Box>
      )}

      <Modal open={addSizeOpen} onClose={() => setAddSizeOpen(false)}>
        <ModalDialog sx={{ maxWidth: 360 }}>
          <Typography level="h4" sx={{ mb: 2, color: 'primary.500' }}>Add size</Typography>
          {addSizeError && (
            <Alert color="danger" sx={{ mb: 2 }}>{addSizeError}</Alert>
          )}
          <form onSubmit={handleAddSizeSubmit}>
            <FormControl sx={{ mb: 2 }} required>
              <FormLabel>Size</FormLabel>
              <Input
                value={newSize.size}
                onChange={(e) => setNewSize((p) => ({ ...p, size: e.target.value }))}
                placeholder="e.g. 10mm"
                sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Description</FormLabel>
              <Input
                value={newSize.desc}
                onChange={(e) => setNewSize((p) => ({ ...p, desc: e.target.value }))}
                placeholder="Optional"
                sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
              />
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button type="button" variant="plain" color="neutral" onClick={() => setAddSizeOpen(false)}>Cancel</Button>
              <Button type="submit" loading={addSizeSubmitting} sx={{ backgroundColor: 'primary.500', '&:hover': { backgroundColor: 'primary.600' } }}>Add</Button>
            </Box>
          </form>
        </ModalDialog>
      </Modal>
    </FormControl>
  );
};

export default SizeSelect;
